import {
  Avatar,
  Badge,
  Box,
  Chip,
  Grid,
  InputAdornment,
  InputLabel,
  List,
  ListItemButton,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import socket from "../Utils/socket";
import emptyList from "../assets/Icons/emptylist.svg";
import { useDispatch } from "react-redux";
import { RiSearch2Line } from "react-icons/ri";

const ChatList = () => {
  const router = useLocation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [list, setList] = useState(null);
  const [empty, setEmpty] = useState(false);
  const [loading, setLoading] = useState(true);

  const MY_OFFERS = "myUserList";
  const GETOFFERS = "getMyUserList";

  useEffect(() => {
    // Ensure loading is true initially
    setLoading(true);

    if (!socket.connected) {
      socket.connect();
    }

    socket.emit(GETOFFERS);

    socket.on(MY_OFFERS, (data) => {
      setLoading(false); // Stop loading

      if (!data || data.length === 0) {
        setEmpty(true);
        setList([]);
      } else {
        setEmpty(false);
        setList(data);
      }
    });

    // Clean up the event listener when the component unmounts
    return () => {
      socket.off(MY_OFFERS);
    };
  }, []); // Only run once when the component mounts

  function formatDateLabel(isoString) {
    const inputDate = new Date(isoString);
    const now = new Date();

    // Reset times for date-only comparison
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const inputDay = new Date(
      inputDate.getFullYear(),
      inputDate.getMonth(),
      inputDate.getDate(),
    );

    const diffDays = Math.floor((today - inputDay) / (1000 * 60 * 60 * 24));

    // Today → return 24hr time
    if (diffDays === 0) {
      return inputDate.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    }

    // Yesterday
    if (diffDays === 1) {
      return "Yesterday";
    }

    // Otherwise → weekday name
    return inputDate.toLocaleDateString("en-GB", {
      weekday: "long",
    });
  }

  const getLastMessageTime = (item) => {
    const ts =
      item?.lastMessage?.updatedAt ||
      item?.lastMessage?.createdAt ||
      item?.updatedAt ||
      item?.createdAt ||
      0;
    return new Date(ts).getTime();
  };



  const sortedList = useMemo(() => {
    if (!list) return [];
    return [...list].sort(
      (a, b) => getLastMessageTime(b) - getLastMessageTime(a),
    );
  }, [list]);

  const getInitials = (user) => {
    if (!user) return "?";
    return `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase();
  };

  return (
    <Box
      className="hide_scrollbar"
      sx={{
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <div style={{ borderBottom: "0.5px solid #E5E0DC" }}>
        <div className="px-6 pb-6">
          <p className="nunito text-[24px] font-[900] my-0 mb-5">Chat</p>

          <TextField
            fullWidth
            placeholder="Search Chats..."
            size="small"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <RiSearch2Line className="w-4 h-4" />
                  </InputAdornment>
                ),
                style: {
                  height: "42px",
                  borderRadius: "50px",
                },
              },
            }}
          />
        </div>
      </div>

      <div className="px-2 overflow-auto h-[600px]">
        {/* Show Empty State when list is empty */}
        {empty && !loading && (
          <Box
            sx={{
              height: { lg: "50vh", md: "50vh", sm: "auto", xs: "auto" },
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img src={emptyList} alt="No Chats Available" />
          </Box>
        )}

        {/* Show skeleton while loading */}
        {loading && (
          <>
            {[...Array(5)].map((_, index) => (
              <Box key={index} sx={{ py: 2, display: "flex", columnGap: 2 }}>
                <Skeleton variant="circular" width={45} height={45} />
                <Box sx={{ width: "70%" }}>
                  <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                  <Skeleton
                    variant="text"
                    sx={{ fontSize: "0.5rem", width: "50%" }}
                  />
                </Box>
              </Box>
            ))}
          </>
        )}

        {/* Render the chat list if available */}
        {!loading && list?.length > 0 && (
          <List>
            {sortedList.map((item, index) => {
              const unreadCount = Number(item?.chat || item?.unreadCount || 0);
              const lastMessageText = item?.lastMessage?.message || "";

              return (
                <Link
                  key={index}
                  to={
                    isMobile
                      ? `/chat-mobile/${item?.roomChannel}?name=${item?.user?.firstName} ${item?.user?.lastName}`
                      : `/chat/${item?.roomChannel}?name=${item?.user?.firstName} ${item?.user?.lastName}`
                  }
                  style={{ cursor: "pointer", textDecoration: "none" }}
                >
                  <ListItemButton
                    selected={router.pathname === `/chat/${item?.roomChannel}`}
                    sx={{
                      // borderBottom: "1px solid #F2F2F2",
                      cursor: "pointer",
                      py: 2,
                      borderRadius: "8px",
                      "&.Mui-selected, :hover": {
                        bgcolor: "#EBDCEF",
                        borderLeft: "4px solid #2E1834",
                      },
                    }}
                  >
                    <Grid container spacing={1} alignItems="center">
                      <Grid
                        item
                        lg={2}
                        md={2}
                        xs={2}
                        sx={{ display: "flex", placeItems: "center" }}
                      >
                        <Tooltip
                          title={`${item?.user?.firstName || ""} ${item?.user?.lastName || ""}`}
                        >
                          <Avatar
                            src={item?.user?.imageUrl || undefined}
                            alt={`${item?.user?.firstName || "User"}`}
                            sx={{
                              width: 40,
                              height: 40,
                              fontSize: 16,
                              fontWeight: 600,
                              border: "1px solid ",
                              borderColor: "primary.main",
                              bgcolor: item?.user?.imageUrl
                                ? "transparent"
                                : "primary.main",
                              color: "white",
                            }}
                          >
                            {!item?.user?.imageUrl && getInitials(item?.user)}
                          </Avatar>
                        </Tooltip>
                      </Grid>
                      <Grid item lg={8} md={8} xs={8} sx={{ pl: 2 }}>
                        <InputLabel
                          sx={{
                            color: "#000",
                            fontWeight: 600,
                            fontSize: "16px",
                          }}
                        >
                          {` ${item?.user?.firstName} ${item?.user?.lastName}`}
                        </InputLabel>

                        <p className="truncate text-gray-500 text-[13px] my-0 mt-1">
                          {lastMessageText}
                        </p>
                      </Grid>
                      <Grid item lg={2} align="right">
                        <Typography sx={{ fontSize: "8px", color: "#333" }}>
                          {formatDateLabel(item?.lastMessage?.updatedAt)}
                        </Typography>
                        <Badge
                          color="primary"
                          badgeContent={unreadCount}
                          max={99}
                          invisible={unreadCount === 0}
                        />
                      </Grid>
                    </Grid>
                  </ListItemButton>
                </Link>
              );
            })}
          </List>
        )}
      </div>
    </Box>
  );
};

export default ChatList;
